import type { JSONContent, MarkdownRendererHelpers } from '@tiptap/core';

import {
  isEmptySectionNode,
  withEmptyHeadingMarkerIdentity,
} from './sections/section-markdown';
import { SECTION_END_MARKER } from './sections/section-markers';

const BLOCK_SEPARATOR = '\n\n';

/**
 * Doc-level markdown render. `@tiptap/markdown` serializes the doc
 * by dispatching to this handler, which is the only place with sibling order in
 * view — a section node can't see its next sibling. Top-level blocks join with
 * the standard blank line; a `SECTION_END_MARKER` is inserted after a section
 * that is immediately followed by a non-section block, so text typed outside a
 * section survives reload instead of being re-absorbed into the section above.
 *
 * The section-end marker is conditional: a section followed by another section
 * (or EOF) gets none, so it adds no bytes to fully-sectioned notes.
 */
export const renderDocumentMarkdown = (
  node: JSONContent,
  helpers: MarkdownRendererHelpers
): string => {
  const children = node.content ?? [];
  const parts: string[] = [];
  let emptyHeadingMarkerIdentity = 0;

  children.forEach((child, index) => {
    const childToRender = isEmptySectionNode(child)
      ? withEmptyHeadingMarkerIdentity(child, ++emptyHeadingMarkerIdentity)
      : child;
    parts.push(helpers.renderChildren([childToRender], ''));
    const next = children[index + 1];
    const closesSection =
      child.type === 'sectionNode' && next !== undefined && next.type !== 'sectionNode';
    if (closesSection) parts.push(SECTION_END_MARKER);
  });

  return parts.filter((part) => part.length > 0).join(BLOCK_SEPARATOR);
};

export const documentMarkdownSpec = {
  renderMarkdown: renderDocumentMarkdown,
};
